export class Accordion {
    /** @type {HTMLDetailsElement | null} */
    detailsElement = null
  
    /** @type {HTMLDivElement | null} */
    contentElement = null
  
    /** @type {string | null} */
    groupId = null
  
    /** @type {HTMLElement | null} */
    summaryElement = null

    static instanceCount = 0;

  
    /**
     * @param {HTMLDetailsElement} detailsElement
     */
    constructor (detailsElement) {
      Accordion.instanceCount++;
      this.isFirstInstance = Accordion.instanceCount === 1;
      this.detailsElement = detailsElement
      this.summaryElement = detailsElement.querySelector('[data-accordion-summary]')
      this.contentElement = detailsElement.querySelector('[data-accordion-content]')
      this.handleResize = this.handleResize.bind(this);
    
      // Add the event listener for the 'resize' event
      window.addEventListener('resize', this.handleResize);
  
      if ('accordionGroup' in detailsElement.dataset) {
        this.groupId = detailsElement.dataset.accordionGroup
      }
  
      this.detailsElement.accordion = this
      this.checkIfFirstAccordionIsOpen()
  
      this.summaryElement.addEventListener('click', this.accordionInit.bind(this))
    }
  
    /**
     * For determining if other accordions need to be collapsed
     *
     * @return {boolean}
     */
    get isPartOfGroup () {
      return this.groupId !== null
    }
  
    /**
     * For animation calculations
     *
     * @return {string}
     */
    get targetHeight () {
      const height = this.contentElement.offsetHeight + this.summaryElement.offsetHeight
      return `${height}px`
    }
  
    /**
     * For animation calculations
     *
     * @return {string}
     */
    get closedHeight () {
      return `${this.summaryElement.offsetHeight}px`
    }

    /**
     * Instance method for initial opened accordion animation
     *
     * @return {void}
     */
    checkIfFirstAccordionIsOpen() {
      if (this.isFirstInstance && this.detailsElement.open) {
        if(this.detailsElement.classList.contains('fancyAccordion')) {
          this.detailsElement.classList.add('-fadeIn')
        }
        this.detailsElement.style.setProperty('--accordion-target-height', this.targetHeight)
      }
    }
  
    /**
     * Initialize accordion on interaction
     *
     * @param {MouseEvent | KeyboardEvent} event
     * @return {void}
     */
    accordionInit (event) {
      event.preventDefault()
  
      this.toggle()
    }
  
    /**
     * Public method to toggle accordion open or closed
     *
     * @return {void}
     */
    toggle () {
      if (this.detailsElement.open) {
        this.close()
      } else {
        if (this.isPartOfGroup) {
          this.closeGroup()
        }
  
        this.open()
      }
    }
  
    /**
     * Public method to close accordion
     *
     * @return {void}
     */
    close () {
        if(this.detailsElement.classList.contains('fancyAccordion')) {
            this.detailsElement.classList.remove('-fadeIn')
        }

        // Wait for fadeOut to complete before closing
        setTimeout(() => {
            this.detailsElement.style.setProperty('--accordion-target-height', this.closedHeight)
            this.detailsElement.removeAttribute('data-accordion-open')
            this.detailsElement.setAttribute('aria-expanded', false)
            
            // Wait for height transition before fully closing
            setTimeout(() => {
                this.detailsElement.open = false
            }, 300) // Match your transition duration
        }, 50)
    }
  
    /**
     * Public method to open accordion
     *
     * @return {void}
     */
    open () {
        this.detailsElement.style.setProperty('--accordion-target-height', this.closedHeight)
        this.detailsElement.style.setProperty('--accordion-closed-height', this.closedHeight)
    
        this.detailsElement.setAttribute('data-accordion-open', null)
        this.detailsElement.setAttribute('aria-expanded', true)
    
        // First RAF: Open the accordion
        requestAnimationFrame(() => {
            this.detailsElement.open = true
            
            // Second RAF: Set height and trigger transitions
            requestAnimationFrame(() => {
                this.detailsElement.style.setProperty('--accordion-target-height', this.targetHeight)
                
                // Wait for height transition to start before adding fadeIn
                setTimeout(() => {
                    if(this.detailsElement.classList.contains('fancyAccordion')) {
                        this.detailsElement.classList.add('-fadeIn')
                    }
                }, 50) // Small delay to ensure height transition has started
                
                // Wait for all transitions to complete before scrolling
                // The accordion has a 300ms transition duration
                setTimeout(() => {
                    this.scrollIntoView(this.detailsElement)
                }, 400) // Slightly longer than the transition duration
            })
        })
    }
  
    /**
     * If accordion is part of a group, close all other accordions in the group
     *
     * @return {void}
     */
    closeGroup () {
      const accordionGroupElements = [...document.querySelectorAll(`[data-accordion-group="${this.groupId}"]`)]
        .filter(el => el !== this.detailsElement)
  
      accordionGroupElements.forEach(el => {
        el.accordion.close()

        if(el.classList.contains('fancyAccordion')) {
          // el.classList.remove('-fadeIn')
        }
      })
    }

  /**
   * Method to scroll into view port, if it's outside the viewport
   *
   * @param {Object} target - DOM Element
   * @returns {undefined}
   */
  scrollIntoView = (target) => {
    // Calculate the scroll position manually
    const targetRect = target.getBoundingClientRect();
    const targetTop = window.pageYOffset + targetRect.top;
    
    // Function to calculate scroll margin based on viewport width
    const getScrollMargin = () => {
        if (window.innerWidth > 1023) {
            return 108; // Desktop Sticky Nav height
        } else {
            return 72; // Mobile Sticky Nav height
        }
    };

    if (target.getBoundingClientRect().bottom > (window.innerHeight || document.documentElement.clientHeight) || target.getBoundingClientRect().top < 0) {
      window.scrollTo({
          top: targetTop - getScrollMargin(),
          behavior: 'smooth'
      });
    }

  };

    // Needed when an accordion is open and screen is resized
    handleResize () {
      if (this.detailsElement.open) {
        this.detailsElement.style.setProperty('--accordion-target-height', this.targetHeight)
      }
    }
  }
  